import { Dispatch, SetStateAction } from "react";
import { VacancyProps } from "storyblok/components";
import Image from "next/legacy/image";
import LeadLogo from "public/images/logos/lhc-circle-logo-2024.svg";
import Link from "next/link";

import { ARROW_RIGHT, Button, CLOCK, Icon, MAP_PIN } from "components/ui";
import { EURO } from "components/ui/icon/IconSet";
import { USER } from "components";

import {
    ApplyButtonWrapper,
    Department,
    DetailedInfo,
    IconWrapper,
    LogoWrapper,
    RecruiterContact,
    Title,
    VacancyInfo,
    Wrapper
} from "./VacancyStickyHeader.styled";

import { getEducationCode } from "helpers/hooks/getEducationCode";
import { useMediaQueryContext } from "helpers/hooks";

const detailIconColor = "var(--color-orange-50)";

export function VacancyStickyHeader({
    visible,
    vacancy,
    showForm,
    showContactForm,
    isZZP,
    salary
}: {
    visible: boolean;
    vacancy: VacancyProps;
    showForm: Dispatch<SetStateAction<boolean>>;
    showContactForm: Dispatch<SetStateAction<boolean>>;
    isZZP: boolean;
    salary: string;
}): JSX.Element {
    const { isDesktopView, isXlDesktopView } = useMediaQueryContext();

    const vacancyDetails = isZZP
        ? {
              city: vacancy?.client.town,
              department: vacancy?.start_date,
              education: vacancy?.niveau?.includes(",")
                  ? getEducationCode(vacancy?.niveau.split(",")[0])
                  : getEducationCode(vacancy?.niveau),
              hours: vacancy?.uren_per_week,
              title: vacancy?.title
          }
        : {
              city: vacancy?.city,
              department: vacancy?.department,
              education: getEducationCode(vacancy?.education_code),
              hours:
                  vacancy.min_hours && vacancy.max_hours
                      ? `${vacancy?.min_hours as string}-${
                            vacancy?.max_hours as string
                        } UUR`
                      : null,
              title: vacancy?.title
          };

    return (
        <Wrapper visible={visible}>
            {isDesktopView && (
                <Link href="/" legacyBehavior>
                    <LogoWrapper>
                        <Image src={LeadLogo} height="48" width="48" />
                    </LogoWrapper>
                </Link>
            )}
            <VacancyInfo>
                {vacancyDetails.title && <Title>{vacancyDetails.title}</Title>}
                {isDesktopView && (
                    <DetailedInfo>
                        {vacancyDetails.department && isXlDesktopView && (
                            <Department>{vacancyDetails.department}</Department>
                        )}

                        {vacancyDetails.hours && (
                            <IconWrapper>
                                <Icon
                                    icon={CLOCK}
                                    size={24}
                                    color={detailIconColor}
                                />
                                {vacancyDetails.hours}
                            </IconWrapper>
                        )}

                        {vacancyDetails.education && (
                            <IconWrapper>
                                <Icon
                                    icon={USER}
                                    size={24}
                                    color={detailIconColor}
                                />
                                {vacancyDetails.education}
                            </IconWrapper>
                        )}

                        {vacancyDetails.city && (
                            <IconWrapper>
                                <Icon
                                    icon={MAP_PIN}
                                    size={24}
                                    color={detailIconColor}
                                />
                                {`REGIO ${vacancyDetails.city}`}
                            </IconWrapper>
                        )}

                        {salary && (
                            <IconWrapper>
                                <Icon
                                    icon={EURO}
                                    size={24}
                                    viewbox="0 0 120 120"
                                    color={detailIconColor}
                                />
                                {salary}
                            </IconWrapper>
                        )}
                    </DetailedInfo>
                )}
            </VacancyInfo>
            {isDesktopView && (
                <RecruiterContact onClick={() => showContactForm(true)}>
                    <p>Vragen?</p>
                </RecruiterContact>
            )}

            <ApplyButtonWrapper>
                <Button
                    buttonType="secondary"
                    icon={ARROW_RIGHT}
                    onClick={() => showForm(true)}
                    reversedOrder
                >
                    Solliciteer
                </Button>
            </ApplyButtonWrapper>
        </Wrapper>
    );
}
